import { type createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';
import {
	type BusinessesAction,
	downloadBlob,
	fulfilledWithBusinessMatcher,
	fulfilledWithSettingsMatcher,
	getApiError,
	isApiFulfilledAction,
	type SettingsAction,
	showToast,
	updatePersistor,
} from 'BreetHelpers';
import {
	apiSlice,
	type AppDispatch,
	clearCountDownStartTimeArray,
	closeModals,
	logUserIn,
	logUserOut,
	resetExportDates,
	type RootState,
	setAuthSettings,
	setBusiness,
	setCountDownStartTimeArray,
	setFcmToken,
	setIs2faAlert,
	setIsApprovedForLive,
	setIsFiatBalanceHidden,
	setShowSecretKey,
	setTargetEnvironment,
	setThemeMode,
} from 'BreetRedux';

export function appListeners(mw: ReturnType<typeof createListenerMiddleware>) {
	const startAppListening = mw.startListening.withTypes<RootState, AppDispatch>();

	startAppListening({
		predicate: isPersistenceAction,
		effect: (_, api) => {
			const state = api.getState();
			updatePersistor({
				countDownStartTimeArray: state.app.countDownStartTimeArray,
				themeMode: state.app.themeMode,
				isUserLoggedIn: state.app.isUserLoggedIn,
				lastVisited: state.app.lastVisited,
				lastVisitedOwner: state.app.lastVisitedOwner,
				fcmToken: state.app.fcmToken,
				isFiatBalanceHidden: state.app.isFiatBalanceHidden,
				showSecretKey: state.app.showSecretKey,
				is2faAlert: state.app.is2faAlert,
				targetEnvironment: state.app.targetEnvironment,
				linkActionTarget: state.app.linkActionTarget,
				isApprovedForLive: state.app.isApprovedForLive,
				business: state.app.business,
			});
		},
	});

	startAppListening({
		predicate: fulfilledWithSettingsMatcher,
		effect: (action, api) => {
			const { payload } = action as unknown as SettingsAction;
			if (payload?.meta?.settings?.fxRates) {
				api.dispatch(setAuthSettings(payload.meta.settings));
			}
		},
	});

	startAppListening({
		predicate: fulfilledWithBusinessMatcher,
		effect: (action, api) => {
			const { payload } = action as unknown as BusinessesAction;
			const businesses = payload?.data?.businesses;
			if (Array.isArray(businesses) && businesses.length > 0) {
				const state = api.getState();

				const userSelectedBusiness = businesses.find((i) => i.id === state.app.business?.id);

				if (userSelectedBusiness) {
					api.dispatch(setBusiness(userSelectedBusiness));
				}
			}
		},
	});

	startAppListening({
		actionCreator: setTargetEnvironment,
		effect: async (_, api) => {
			api.dispatch(apiSlice.util.resetApiState());

			const { evaluateOnboarding } = await import('../../routes/guards');
			await evaluateOnboarding();

			// Re-run the current route's loaders/guards so a Sandbox<->Live switch re-evaluates
			// access for the page the user is already on (e.g. redirect off a now-forbidden route),
			// instead of leaving stale, pre-switch guard results in place until the next navigation.
			const { default: router } = await import('../../router');
			await router.revalidate().catch(() => undefined);
		},
	});

	startAppListening({
		actionCreator: logUserOut,
		effect: (_, api) => {
			api.dispatch(closeModals());
			api.dispatch(apiSlice.util.resetApiState());
		},
	});

	startAppListening({
		predicate: (action) => {
			if (!isApiFulfilledAction(action)) return false;
			const { meta } = action as {
				meta?: { arg?: { endpointName?: string; originalArgs?: { exports?: boolean } } };
			};
			const endpointName = meta?.arg?.endpointName;
			return !!meta?.arg?.originalArgs?.exports && !!endpointName && BLOB_EXPORT_ENDPOINTS.has(endpointName);
		},
		effect: (action, api) => {
			const { payload } = action as unknown as { payload: unknown };

			if (payload instanceof Blob) {
				if (payload.size === 0) {
					showToast('error', 'No data found for the selected date range.');
				} else {
					downloadBlob(payload, `breet-export-${new Date().toISOString().slice(0, 10)}.csv`);
					showToast('success', 'Export downloaded successfully.');
				}
			} else {
				const message = (payload as { message?: string } | null)?.message;
				showToast('success', message ?? 'Your export is on its way — check your email shortly.');
			}

			api.dispatch(resetExportDates());
		},
	});

	startAppListening({
		predicate: (action) => {
			const { type } = action as { type?: unknown };
			if (typeof type !== 'string' || !type.startsWith('api/') || !type.endsWith('/rejected')) return false;

			const { meta } = action as {
				meta?: { arg?: { endpointName?: string; originalArgs?: { exports?: boolean } } };
			};
			const endpointName = meta?.arg?.endpointName;

			return !!meta?.arg?.originalArgs?.exports && !!endpointName && BLOB_EXPORT_ENDPOINTS.has(endpointName);
		},
		effect: (action, api) => {
			const { payload } = action as unknown as { payload: unknown };

			showToast('error', getApiError(payload) || 'Export failed. Please try again.');

			api.dispatch(resetExportDates());
		},
	});
}

const BLOB_EXPORT_ENDPOINTS = new Set<string>([
	'getTransactionsEditServices',
	'getTransactionsSellServices',
	'getTradesSubAccountWalletAddresses',
	'getWebhookLogs',
]);

const isPersistenceAction = isAnyOf(
	clearCountDownStartTimeArray,
	setCountDownStartTimeArray,
	setThemeMode,
	setIsFiatBalanceHidden,
	setFcmToken,
	setShowSecretKey,
	logUserIn,
	logUserOut,
	setIs2faAlert,
	setTargetEnvironment,
	setIsApprovedForLive,
	setBusiness
);
